import { useState, useEffect } from 'react';
import { useAccount, useReadContract } from 'wagmi';
import { formatUnits, parseUnits } from 'viem';
import { CONTRACT_ADDRESSES, MASTER_CHEF_ABI, PAIR_ABI, FTSO_REGISTRY_ABI, ROUTER_ABI } from '../config/contracts';
import { BACKEND_URL } from '../config/api';
import { getTokenLogo } from '../config/tokens';

// Per-pool card component with live on-chain stats
function FarmCardWithStats({ farm, totalAllocPoint, rewardPerBlock, flrPriceData, emberPriceData, isConnected, address, onNavigateToFarm }) {
  const lpAddress = farm.lpTokenAddress || null;

  const { data: totalStaked } = useReadContract({
    address: lpAddress,
    abi: PAIR_ABI,
    functionName: 'balanceOf',
    args: lpAddress && CONTRACT_ADDRESSES.MASTER_CHEF ? [CONTRACT_ADDRESSES.MASTER_CHEF] : undefined,
    query: {
      enabled: !!(lpAddress && CONTRACT_ADDRESSES.MASTER_CHEF),
      refetchInterval: 30000,
      retry: false,
      throwOnError: false,
    },
  });

  const { data: lpTotalSupply } = useReadContract({
    address: lpAddress,
    abi: PAIR_ABI,
    functionName: 'totalSupply',
    query: {
      enabled: !!lpAddress,
      refetchInterval: 30000,
      retry: false,
      throwOnError: false,
    },
  });

  const { data: reserves } = useReadContract({
    address: lpAddress,
    abi: PAIR_ABI,
    functionName: 'getReserves',
    query: {
      enabled: !!lpAddress,
      refetchInterval: 30000,
      retry: false,
      throwOnError: false,
    },
  });

  const { data: userInfo } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'userInfo',
    args: address && farm.pid !== undefined ? [farm.pid, address] : undefined,
    query: {
      enabled: !!(address && CONTRACT_ADDRESSES.MASTER_CHEF && isConnected),
      refetchInterval: 30000,
      retry: false,
      throwOnError: false,
    },
  });

  const { data: pendingReward } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'pendingReward',
    args: address && farm.pid !== undefined ? [farm.pid, address] : undefined,
    query: {
      enabled: !!(address && CONTRACT_ADDRESSES.MASTER_CHEF && isConnected),
      refetchInterval: 30000,
      retry: false,
      throwOnError: false,
    },
  });

  const calculateAPR = () => {
    if (!rewardPerBlock || !totalAllocPoint || !totalStaked) return '—';
    try {
      const allocPoint = farm.allocPoint || 0;
      const BLOCKS_PER_YEAR = 17520000;
      const poolShare = Number(allocPoint) / Number(totalAllocPoint);
      const annualEmberRewards = Number(formatUnits(rewardPerBlock, 18)) * BLOCKS_PER_YEAR * poolShare;

      const flrPriceUSD = flrPriceData?.[0] && flrPriceData?.[2]
        ? Number(formatUnits(flrPriceData[0], Number(flrPriceData[2])))
        : 0;
      const emberPriceInWFLR = emberPriceData?.[1]
        ? Number(formatUnits(emberPriceData[1], 18))
        : 0;
      const emberPriceUSD = emberPriceInWFLR * flrPriceUSD;

      let lpTokenPriceUSD = 0;
      if (reserves && lpTotalSupply && flrPriceUSD > 0) {
        const reserve1 = Number(formatUnits(reserves[1], 18));
        const totalSupply = Number(formatUnits(lpTotalSupply, 18));
        lpTokenPriceUSD = totalSupply > 0 ? (reserve1 * flrPriceUSD * 2) / totalSupply : 0;
      }

      const stakedLPAmount = Number(formatUnits(totalStaked, 18));

      if (emberPriceUSD === 0 || lpTokenPriceUSD === 0) {
        if (stakedLPAmount === 0) return '∞%';
        const apr = (annualEmberRewards / stakedLPAmount) * 100;
        if (apr > 999999) return '∞%';
        if (apr > 10000) return `${(apr / 1000).toFixed(0)}K%`;
        if (apr > 1000) return `${(apr / 1000).toFixed(1)}K%`;
        return `${apr.toFixed(2)}%`;
      }

      const totalStakedUSD = stakedLPAmount * lpTokenPriceUSD;
      if (totalStakedUSD === 0) return '∞%';
      const apr = (annualEmberRewards * emberPriceUSD / totalStakedUSD) * 100;
      if (apr > 999999) return '∞%';
      if (apr > 10000) return `${(apr / 1000).toFixed(0)}K%`;
      if (apr > 1000) return `${(apr / 1000).toFixed(1)}K%`;
      return `${apr.toFixed(2)}%`;
    } catch {
      return '—';
    }
  };

  const apr = calculateAPR();
  const totalStakedLP = totalStaked ? parseFloat(formatUnits(totalStaked, 18)) : null;
  const stakedAmount = userInfo ? parseFloat(formatUnits(userInfo[0], 18)) : 0;
  const earnedAmount = pendingReward ? parseFloat(formatUnits(pendingReward, 18)) : 0;

  const getTier = (allocPoint) => {
    if (allocPoint >= 1000) return { tier: 'High', color: '#FB923C', bgColor: 'rgba(251, 146, 60, 0.2)' };
    if (allocPoint >= 500) return { tier: 'Medium', color: '#FFB84D', bgColor: 'rgba(255, 184, 77, 0.2)' };
    return { tier: 'Standard', color: '#9ca3af', bgColor: 'rgba(156, 163, 175, 0.2)' };
  };
  const tierInfo = getTier(farm.allocPoint);

  const tokens = farm.name.replace(/\s*(LP|lp)\s*$/i, '').split(/[\/\- ]+/);
  const token0 = tokens[0]?.trim();
  const token1 = tokens[1]?.trim();

  return (
    <div
      style={{
        backgroundColor: 'rgba(15, 8, 30, 0.92)',
        border: '1px solid rgba(139, 92, 246, 0.35)',
        borderRadius: '12px',
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 0 14px rgba(139, 92, 246, 0.2)',
        position: 'relative',
        overflow: 'hidden',
      }}
      onClick={() => onNavigateToFarm && onNavigateToFarm(farm)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.6)';
        e.currentTarget.style.boxShadow = '0 0 24px rgba(139, 92, 246, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.35)';
        e.currentTarget.style.boxShadow = '0 0 14px rgba(139, 92, 246, 0.2)';
      }}
    >
      {/* Active indicator */}
      {farm.isActive && (
        <div style={{
          position: 'absolute', top: '12px', right: '12px',
          width: '8px', height: '8px', borderRadius: '50%',
          backgroundColor: '#4ade80', boxShadow: '0 0 8px rgba(74, 222, 128, 0.6)',
        }} />
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
          {token0 && token1 && (
            <div style={{ display: 'flex', marginLeft: '-8px' }}>
              {[token0, token1].map((token, i) => (
                <div key={i} style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  border: '2px solid #0a0514', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '14px', fontWeight: '700', color: '#fff',
                  marginLeft: i > 0 ? '-12px' : '0', zIndex: 10 - i,
                  backgroundColor: '#1a1a1a', overflow: 'hidden',
                }}>
                  {getTokenLogo(token)
                    ? <img src={getTokenLogo(token)} alt={token} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : token.charAt(0)
                  }
                </div>
              ))}
            </div>
          )}
          <div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#fff', marginBottom: '4px' }}>{farm.name}</div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>Stake {farm.lpTokenAddress ? 'LP tokens' : farm.name}</div>
          </div>
        </div>
        <div style={{ backgroundColor: tierInfo.bgColor, color: tierInfo.color, padding: '4px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '700' }}>
          {tierInfo.tier} Tier
        </div>
      </div>

      {/* APR */}
      <div style={{ backgroundColor: 'rgba(74, 222, 128, 0.1)', border: '1px solid rgba(74, 222, 128, 0.3)', borderRadius: '8px', padding: '12px', marginBottom: '16px', textAlign: 'center' }}>
        <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>APR</div>
        <div style={{ fontSize: '28px', fontWeight: '700', color: '#4ade80' }}>{apr}</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>Total Staked</div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#fff' }}>
            {totalStakedLP !== null ? `${totalStakedLP.toFixed(2)} LP` : '—'}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>Earning Tier</div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: tierInfo.color }}>{tierInfo.tier}</div>
        </div>
      </div>

      {/* User position */}
      {isConnected && (stakedAmount > 0 || earnedAmount > 0) && (
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(139, 92, 246, 0.2)' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#A78BFA', marginBottom: '8px' }}>Your Position</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
            <span style={{ color: '#9ca3af' }}>Staked</span>
            <span style={{ color: '#fff', fontWeight: '600' }}>{stakedAmount.toFixed(4)} LP</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
            <span style={{ color: '#9ca3af' }}>Earned</span>
            <span style={{ color: '#4ade80', fontWeight: '600' }}>{earnedAmount.toFixed(4)} EMBER</span>
          </div>
        </div>
      )}
    </div>
  );
}

function FarmsOverview({ isConnected, onNavigateToFarm }) {
  const { address } = useAccount();
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [sortBy, setSortBy] = useState('apr');

  // Fetch farm pools
  useEffect(() => {
    const fetchFarms = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/farm-pools/list`);
        const data = await response.json();
        setFarms(data.pools || []);
      } catch (error) {
        console.error('Error fetching farms:', error);
        setFarms([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFarms();
  }, []);

  const { data: totalAllocPoint } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'totalAllocPoint',
    query: {
      enabled: !!CONTRACT_ADDRESSES.MASTER_CHEF,
      refetchInterval: 60000,
      retry: false,
      throwOnError: false,
    },
  });

  const { data: rewardPerBlock } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'rewardPerBlock',
    query: {
      enabled: !!CONTRACT_ADDRESSES.MASTER_CHEF,
      refetchInterval: 60000,
      retry: false,
      throwOnError: false,
    },
  });

  const { data: flrPriceData } = useReadContract({
    address: CONTRACT_ADDRESSES.FTSO_REGISTRY,
    abi: FTSO_REGISTRY_ABI,
    functionName: 'getCurrentPriceWithDecimals',
    args: ['FLR'],
    query: {
      enabled: !!CONTRACT_ADDRESSES.FTSO_REGISTRY,
      refetchInterval: 60000,
      retry: false,
      throwOnError: false,
    },
  });

  const { data: emberPriceData } = useReadContract({
    address: CONTRACT_ADDRESSES.ROUTER,
    abi: ROUTER_ABI,
    functionName: 'getAmountsOut',
    args: (CONTRACT_ADDRESSES.EMBER_TOKEN && CONTRACT_ADDRESSES.WFLR)
      ? [parseUnits('1', 18), [CONTRACT_ADDRESSES.EMBER_TOKEN, CONTRACT_ADDRESSES.WFLR]]
      : undefined,
    query: {
      enabled: !!(CONTRACT_ADDRESSES.ROUTER && CONTRACT_ADDRESSES.EMBER_TOKEN && CONTRACT_ADDRESSES.WFLR),
      refetchInterval: 60000,
      retry: false,
      throwOnError: false,
    },
  });

  const emissionsPerBlock = rewardPerBlock
    ? parseFloat(formatUnits(rewardPerBlock, 18)).toFixed(2)
    : '—';

  const filteredFarms = farms
    .filter(farm => {
      const matchesSearch = farm.name.toLowerCase().includes(searchTerm.toLowerCase());
      if (filterBy === 'all') return matchesSearch;
      if (filterBy === 'live') return matchesSearch && farm.isActive;
      return matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'multiplier') return (b.allocPoint || 0) - (a.allocPoint || 0);
      return 0;
    });

  const activeFarms = farms.filter(f => f.isActive).length;

  return (
    <div className="content-section active">
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 15px' }}>
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#C4B5FD', marginBottom: '8px', textAlign: 'left' }}>Earn</h1>
          <p style={{ fontSize: '14px', color: '#7C5BB0', textAlign: 'left' }}>Stake LP tokens to earn EMBER rewards</p>
        </div>

        {/* Banner */}
        <div style={{ backgroundColor: 'rgba(251, 146, 60, 0.15)', border: '1px solid rgba(251, 146, 60, 0.4)', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontSize: '24px' }}>🔥</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '700', color: '#FB923C', marginBottom: '4px' }}>Earn EMBER Rewards</div>
            <div style={{ fontSize: '13px', color: '#FED7AA' }}>
              {emissionsPerBlock} EMBER per block • {activeFarms} Active Pools
            </div>
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
          <input
            type="text"
            placeholder="Search farms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '12px 16px', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none' }}
          />
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['all', 'live'].map(f => (
              <button key={f} onClick={() => setFilterBy(f)} style={{ flex: '1', minWidth: '70px', padding: '8px 12px', backgroundColor: filterBy === f ? '#FB923C' : '#2a2a2a', color: filterBy === f ? '#1A0800' : '#9ca3af', border: `1px solid ${filterBy === f ? '#FB923C' : '#3a3a3a'}`, borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', textTransform: 'capitalize' }}>
                {f === 'all' ? 'All' : 'Live'}
              </button>
            ))}
            {isConnected && (
              <button onClick={() => setFilterBy('staked')} style={{ flex: '1', minWidth: '70px', padding: '8px 12px', backgroundColor: filterBy === 'staked' ? '#FB923C' : '#2a2a2a', color: filterBy === 'staked' ? '#1A0800' : '#9ca3af', border: `1px solid ${filterBy === 'staked' ? '#FB923C' : '#3a3a3a'}`, borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                Staked
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[['apr', 'APR'], ['tvl', 'TVL'], ['multiplier', 'Earning Tier']].map(([val, label]) => (
              <button key={val} onClick={() => setSortBy(val)} style={{ flex: '1', minWidth: '80px', padding: '8px 12px', backgroundColor: sortBy === val ? '#8B5CF6' : '#2a2a2a', color: sortBy === val ? '#fff' : '#9ca3af', border: `1px solid ${sortBy === val ? '#8B5CF6' : '#3a3a3a'}`, borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Farms */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading farms...</div>
          </div>
        ) : filteredFarms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌾</div>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No farms found</div>
            <div style={{ fontSize: '14px' }}>{searchTerm ? 'Try adjusting your search or filters' : 'Check back later for new farms!'}</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {filteredFarms.map((farm) => (
              <FarmCardWithStats
                key={farm.pid}
                farm={farm}
                totalAllocPoint={totalAllocPoint}
                rewardPerBlock={rewardPerBlock}
                flrPriceData={flrPriceData}
                emberPriceData={emberPriceData}
                isConnected={isConnected}
                address={address}
                onNavigateToFarm={onNavigateToFarm}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FarmsOverview;
